import { Menu, Popover, Progress, Tooltip, Upload } from 'antd';
import React from 'react';
import { RcFile } from 'antd/lib/upload';
import { AttachmentDto } from '@api/Api';
import { downloadImage } from '@helpers/download';
import { ReactComponent as ActionSvg } from '@assets/icons/action.svg';
import { ReactComponent as ExternalLinkSvg } from '@assets/icons/external-link.svg';
import { ReactComponent as AddNewAttachmentsSvg } from '@assets/icons/add-new-attachments-plus.svg';
import { ReactComponent as AudioPlaceHolderSvg } from '@assets/icons/AssetPlaceholder/audiofile.svg';
import { ReactComponent as VideoPlaceHolderSvg } from '@assets/icons/AssetPlaceholder/videofile.svg';
import { ReactComponent as DocumentPlaceHolderSvg } from '@assets/icons/AssetPlaceholder/pdffile.svg';

import './CommentAttachments.less';

type CommentAttachmentsProps = {
  files: (AttachmentDto & { progress?: number })[];
  mode: 'view' | 'edit' | 'upload';
  onDeleteFile: (file: AttachmentDto) => void;
  onAddFile?: (file: RcFile) => void;
};

function FilePreviewField({
  mimeType,
  url
}: {
  mimeType: string;
  url: string;
}) {
  if (mimeType?.includes('audio')) return <AudioPlaceHolderSvg />;
  if (mimeType?.includes('video')) return <VideoPlaceHolderSvg />;
  if (mimeType?.includes('image') && url) return <img src={url} alt="" />;
  return <DocumentPlaceHolderSvg />;
}

export default function CommentAttachments({
  files,
  mode,
  onDeleteFile,
  onAddFile
}: CommentAttachmentsProps) {
  return (
    <div className="comment-attachments">
      {files.map((item) => {
        const isShowProgress = item.progress || item.progress === 0;
        const menuItems = [
          {
            key: 'view-original',
            label: <span>View original</span>,
            onClick: () => window.open(item.url, '_blank')
          },

          {
            key: 'download',
            label: <span>Download</span>,
            onClick: () => downloadImage(item.url, item.name)
          },
          {
            key: 'remove',
            label: <span>Remove</span>,
            onClick: () => onDeleteFile(item)
          }
        ];

        return (
          <Tooltip
            key={item.id}
            overlayClassName="rich_text_form_tooltip"
            overlayStyle={{ position: 'fixed' }}
            overlayInnerStyle={{ maxWidth: 220, textAlign: 'left' }}
            title={item.name}
            placement="topLeft"
          >
            <div
              className="comment-attachments_file"
              onClick={(e) => e.stopPropagation()}
            >
              {mode !== 'view' && (
                <Popover
                  placement="rightTop"
                  trigger={isShowProgress ? [] : ['click']}
                  content={
                    <Menu
                      selectable={false}
                      prefixCls="media_viewer_menu-menu"
                      items={menuItems}
                    />
                  }
                  overlayClassName="media_viewer_information_overlay"
                >
                  <div className="comment-attachments_file-overlay">
                    <ActionSvg className="comment-attachments_file-overlay-icon" />
                  </div>
                  <FilePreviewField mimeType={item.mimeType} url={item.url} />
                  {isShowProgress && (
                    <div className="comment-attachments_file-progress">
                      <Progress
                        type="circle"
                        percent={item.progress}
                        width={50}
                        strokeColor="#3553db"
                        trailColor="rgba(245, 246, 245, 0.5)"
                        strokeWidth={10}
                        success={{
                          strokeColor: '#3553db'
                        }}
                      />
                    </div>
                  )}
                </Popover>
              )}
              {mode === 'view' && (
                <>
                  <div
                    className="comment-attachments_file-overlay"
                    onClick={() => window.open(item.url, '_blank')}
                  >
                    {mode === 'view' && <ExternalLinkSvg />}
                  </div>
                  <FilePreviewField mimeType={item.mimeType} url={item.url} />
                </>
              )}
            </div>
          </Tooltip>
        );
      })}
      {mode === 'upload' && (
        <Upload
          multiple
          showUploadList={false}
          className="comment-attachments_upload"
          beforeUpload={(file) => {
            onAddFile?.(file);
            return false;
          }}
        >
          <AddNewAttachmentsSvg />
        </Upload>
      )}
    </div>
  );
}
